/**
 * FIXTURE DEFINITIONS BY AREA TYPE
 * 
 * Complete fixture definitions for all 16 area types
 * Each fixture follows the pattern: { key, label, condition, details }
 */

// Fixture condition options (universal for all fixtures)
export const FIXTURE_CONDITIONS = [
  { value: 'na', label: 'N/A - Not Present', color: 'slate' },
  { value: 'exists_keep', label: 'Exists - Keep As-Is', color: 'blue' },
  { value: 'exists_replace', label: 'Exists - Replace', color: 'amber' },
  { value: 'add_new', label: 'Add New', color: 'emerald' },
];

// ============================================
// BATHROOM FIXTURES (21 Items)
// ============================================
export const BATHROOM_FIXTURES = {
  // VANITY & SINK
  vanity: {
    key: 'vanity',
    label: 'Vanity Cabinet',
    category: 'Vanity & Sink',
    details: {
      type: { type: 'select', label: 'Type', options: ['single', 'double', 'floating', 'pedestal'] },
      width_inches: { type: 'select', label: 'Width', options: [24, 30, 36, 48, 60, 72] },
      material: { type: 'select', label: 'Material', options: ['wood', 'mdf', 'particle', 'plywood'] },
      door_style: { type: 'select', label: 'Door Style', options: ['shaker', 'flat', 'raised', 'slab'] },
    },
  },
  vanity_top: {
    key: 'vanity_top',
    label: 'Vanity Top / Counter',
    category: 'Vanity & Sink',
    details: {
      material: { type: 'select', label: 'Material', options: ['granite', 'quartz', 'marble', 'laminate', 'solid_surface', 'cultured_marble'] },
      edge: { type: 'select', label: 'Edge', options: ['square', 'beveled', 'bullnose', 'ogee'] },
      integrated_sink: { type: 'checkbox', label: 'Integrated Sink' },
    },
  },
  sink: {
    key: 'sink',
    label: 'Sink (if not integrated)',
    category: 'Vanity & Sink',
    details: {
      type: { type: 'select', label: 'Type', options: ['undermount', 'drop_in', 'vessel', 'pedestal'] },
      material: { type: 'select', label: 'Material', options: ['porcelain', 'ceramic', 'glass', 'stainless', 'composite'] },
      count: { type: 'number', label: 'Count', min: 1, max: 2 },
    },
  },
  sink_faucet: {
    key: 'sink_faucet',
    label: 'Sink Faucet',
    category: 'Vanity & Sink',
    details: {
      type: { type: 'select', label: 'Type', options: ['single_hole', 'widespread', 'centerset', 'wall_mount'] },
      finish: { type: 'select', label: 'Finish', options: ['chrome', 'brushed_nickel', 'matte_black', 'oil_rubbed', 'polished_nickel'] },
      count: { type: 'number', label: 'Count', min: 1, max: 2 },
    },
  },

  // TOILET
  toilet: {
    key: 'toilet',
    label: 'Toilet',
    category: 'Toilet',
    details: {
      type: { type: 'select', label: 'Type', options: ['two_piece', 'one_piece', 'wall_hung'] },
      height: { type: 'select', label: 'Height', options: ['standard', 'comfort_height'] },
      flush: { type: 'select', label: 'Flush', options: ['single', 'dual', 'pressure_assist'] },
    },
  },
  bidet: {
    key: 'bidet',
    label: 'Bidet / Bidet Seat',
    category: 'Toilet',
    details: {
      type: { type: 'select', label: 'Type', options: ['standalone', 'seat_attachment', 'integrated'] },
    },
  },

  // TUB
  tub: {
    key: 'tub',
    label: 'Bathtub',
    category: 'Tub',
    details: {
      type: { type: 'select', label: 'Type', options: ['alcove', 'freestanding', 'drop_in', 'corner', 'walk_in'] },
      material: { type: 'select', label: 'Material', options: ['acrylic', 'fiberglass', 'cast_iron', 'steel', 'stone'] },
      length_inches: { type: 'select', label: 'Length', options: [60, 66, 72] },
      has_jets: { type: 'checkbox', label: 'Has Jets' },
    },
  },
  tub_faucet: {
    key: 'tub_faucet',
    label: 'Tub Faucet / Filler',
    category: 'Tub',
    details: {
      type: { type: 'select', label: 'Type', options: ['deck_mount', 'wall_mount', 'freestanding', 'roman'] },
      finish: { type: 'select', label: 'Finish', options: ['chrome', 'brushed_nickel', 'matte_black', 'oil_rubbed', 'polished_nickel'] },
      has_handheld: { type: 'checkbox', label: 'Has Handheld' },
    },
  },

  // SHOWER
  shower_base: {
    key: 'shower_base',
    label: 'Shower Base / Pan',
    category: 'Shower',
    details: {
      type: { type: 'select', label: 'Type', options: ['acrylic', 'fiberglass', 'tile', 'solid_surface', 'cast_iron'] },
      size: { type: 'select', label: 'Size', options: ['32x32', '36x36', '48x36', '60x32', '60x36', 'custom'] },
      drain_location: { type: 'select', label: 'Drain Location', options: ['center', 'corner', 'linear'] },
    },
  },
  shower_walls: {
    key: 'shower_walls',
    label: 'Shower Walls / Surround',
    category: 'Shower',
    details: {
      type: { type: 'select', label: 'Type', options: ['tile', 'acrylic', 'fiberglass', 'solid_surface', 'stone'] },
      tile_size: { type: 'select', label: 'Tile Size (if tile)', options: ['3x6', '4x12', '12x24', 'large_format', 'mosaic'] },
      pattern: { type: 'select', label: 'Pattern (if tile)', options: ['stack', 'brick', 'herringbone', 'diagonal'] },
    },
  },
  shower_door: {
    key: 'shower_door',
    label: 'Shower Door / Enclosure',
    category: 'Shower',
    details: {
      type: { type: 'select', label: 'Type', options: ['frameless', 'semi_frameless', 'framed', 'bypass', 'curtain'] },
      glass: { type: 'select', label: 'Glass', options: ['clear', 'frosted', 'rain', 'patterned'] },
      width_inches: { type: 'number', label: 'Width (inches)', min: 24, max: 72 },
    },
  },
  shower_fixture: {
    key: 'shower_fixture',
    label: 'Shower Fixture / Valve',
    category: 'Shower',
    details: {
      type: { type: 'select', label: 'Type', options: ['standard', 'rain', 'handheld', 'combo', 'multi_head'] },
      valve_type: { type: 'select', label: 'Valve Type', options: ['pressure_balance', 'thermostatic'] },
      finish: { type: 'select', label: 'Finish', options: ['chrome', 'brushed_nickel', 'matte_black', 'oil_rubbed', 'polished_nickel'] },
      body_sprays: { type: 'checkbox', label: 'Body Sprays' },
    },
  },
  shower_niche: {
    key: 'shower_niche',
    label: 'Shower Niche',
    category: 'Shower',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 4 },
      size: { type: 'select', label: 'Size', options: ['single', 'double', 'triple'] },
      material: { type: 'select', label: 'Material', options: ['tile_match', 'prefab', 'stone'] },
    },
  },
  shower_bench: {
    key: 'shower_bench',
    label: 'Shower Bench / Seat',
    category: 'Shower',
    details: {
      type: { type: 'select', label: 'Type', options: ['built_in', 'fold_down', 'freestanding'] },
      material: { type: 'select', label: 'Material', options: ['tile', 'stone', 'teak', 'solid_surface'] },
    },
  },

  // FLOORING & WALLS
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring & Walls',
    details: {
      material: { type: 'select', label: 'Material', options: ['tile', 'vinyl', 'laminate', 'stone'] },
      tile_size: { type: 'select', label: 'Tile Size (if tile)', options: ['12x12', '12x24', '18x18', '24x24', 'mosaic'] },
      heated: { type: 'checkbox', label: 'Heated Floor' },
    },
  },
  walls: {
    key: 'walls',
    label: 'Walls (Non-Shower)',
    category: 'Flooring & Walls',
    details: {
      type: { type: 'select', label: 'Type', options: ['paint', 'tile', 'wainscot', 'wallpaper'] },
      wainscot_height: { type: 'select', label: 'Wainscot Height (if wainscot)', options: [36, 42, 48] },
    },
  },

  // LIGHTING & ELECTRICAL
  vanity_light: {
    key: 'vanity_light',
    label: 'Vanity Light',
    category: 'Lighting & Electrical',
    details: {
      type: { type: 'select', label: 'Type', options: ['bar', 'sconces', 'flush', 'pendant'] },
      count: { type: 'number', label: 'Count', min: 1, max: 4 },
      length_inches: { type: 'number', label: 'Length (inches)', min: 12, max: 60 },
    },
  },
  ceiling_light: {
    key: 'ceiling_light',
    label: 'Ceiling Light',
    category: 'Lighting & Electrical',
    details: {
      type: { type: 'select', label: 'Type', options: ['flush', 'recessed', 'chandelier', 'fan_light'] },
      count: { type: 'number', label: 'Count', min: 1, max: 8 },
    },
  },
  exhaust_fan: {
    key: 'exhaust_fan',
    label: 'Exhaust Fan',
    category: 'Lighting & Electrical',
    details: {
      cfm: { type: 'select', label: 'CFM', options: [50, 80, 110, 150] },
      has_light: { type: 'checkbox', label: 'Has Light' },
      has_heater: { type: 'checkbox', label: 'Has Heater' },
      has_humidity_sensor: { type: 'checkbox', label: 'Has Humidity Sensor' },
    },
  },

  // ACCESSORIES
  mirror: {
    key: 'mirror',
    label: 'Mirror',
    category: 'Accessories',
    details: {
      type: { type: 'select', label: 'Type', options: ['framed', 'frameless', 'medicine_cabinet', 'lighted'] },
      width_inches: { type: 'number', label: 'Width (inches)', min: 12, max: 72 },
      height_inches: { type: 'number', label: 'Height (inches)', min: 12, max: 48 },
    },
  },
  accessories: {
    key: 'accessories',
    label: 'Accessories',
    category: 'Accessories',
    details: {
      towel_bar: { type: 'checkbox', label: 'Towel Bar' },
      towel_ring: { type: 'checkbox', label: 'Towel Ring' },
      robe_hooks: { type: 'number', label: 'Robe Hooks', min: 0, max: 4 },
      tp_holder: { type: 'checkbox', label: 'TP Holder' },
      grab_bars: { type: 'number', label: 'Grab Bars', min: 0, max: 4 },
    },
  },
};

// ============================================
// KITCHEN FIXTURES (18 Items)
// ============================================
export const KITCHEN_FIXTURES = {
  // CABINETS
  base_cabinets: {
    key: 'base_cabinets',
    label: 'Base Cabinets',
    category: 'Cabinets',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 1, max: 100 },
      door_style: { type: 'select', label: 'Door Style', options: ['shaker', 'flat', 'raised', 'slab', 'beadboard'] },
      material: { type: 'select', label: 'Material', options: ['wood', 'mdf', 'plywood', 'thermofoil'] },
      finish: { type: 'select', label: 'Finish', options: ['painted', 'stained', 'natural'] },
      color: { type: 'text', label: 'Color' },
    },
  },
  wall_cabinets: {
    key: 'wall_cabinets',
    label: 'Wall Cabinets',
    category: 'Cabinets',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 1, max: 100 },
      height: { type: 'select', label: 'Height', options: [30, 36, 42] },
      stacked: { type: 'checkbox', label: 'Double Stack to Ceiling' },
    },
  },
  tall_cabinets: {
    key: 'tall_cabinets',
    label: 'Tall / Pantry Cabinets',
    category: 'Cabinets',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 10 },
      type: { type: 'select', label: 'Type', options: ['pantry', 'oven', 'utility'] },
      width_inches: { type: 'select', label: 'Width', options: [18, 24, 30, 36] },
    },
  },
  island: {
    key: 'island',
    label: 'Island / Peninsula',
    category: 'Cabinets',
    details: {
      type: { type: 'select', label: 'Type', options: ['island', 'peninsula'] },
      length_inches: { type: 'number', label: 'Length (inches)', min: 24, max: 144 },
      width_inches: { type: 'number', label: 'Width (inches)', min: 24, max: 60 },
      has_seating: { type: 'checkbox', label: 'Has Seating' },
      has_sink: { type: 'checkbox', label: 'Has Sink' },
      has_cooktop: { type: 'checkbox', label: 'Has Cooktop' },
    },
  },

  // COUNTERTOPS
  countertops: {
    key: 'countertops',
    label: 'Countertops',
    category: 'Countertops',
    details: {
      material: { type: 'select', label: 'Material', options: ['granite', 'quartz', 'marble', 'laminate', 'butcher_block', 'concrete', 'solid_surface'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 1, max: 200 },
      edge: { type: 'select', label: 'Edge', options: ['square', 'beveled', 'bullnose', 'ogee', 'waterfall'] },
      backsplash_height: { type: 'select', label: 'Backsplash Height', options: ['none', '4_inch', 'full'] },
    },
  },
  backsplash: {
    key: 'backsplash',
    label: 'Backsplash',
    category: 'Countertops',
    details: {
      material: { type: 'select', label: 'Material', options: ['tile', 'stone', 'glass', 'metal', 'same_as_counter'] },
      tile_size: { type: 'select', label: 'Tile Size', options: ['3x6', '4x4', '4x12', 'mosaic', 'large_format'] },
      pattern: { type: 'select', label: 'Pattern', options: ['stack', 'brick', 'herringbone', 'diagonal'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 1, max: 100 },
    },
  },

  // SINK & FAUCET
  sink: {
    key: 'sink',
    label: 'Kitchen Sink',
    category: 'Sink & Faucet',
    details: {
      type: { type: 'select', label: 'Type', options: ['undermount', 'drop_in', 'farmhouse', 'integrated'] },
      material: { type: 'select', label: 'Material', options: ['stainless', 'granite_composite', 'fireclay', 'cast_iron', 'copper'] },
      bowls: { type: 'select', label: 'Bowls', options: ['single', 'double', '60_40', 'low_divide'] },
      size: { type: 'select', label: 'Size', options: [25, 30, 33, 36] },
    },
  },
  faucet: {
    key: 'faucet',
    label: 'Kitchen Faucet',
    category: 'Sink & Faucet',
    details: {
      type: { type: 'select', label: 'Type', options: ['pull_down', 'pull_out', 'standard', 'pot_filler', 'touchless'] },
      finish: { type: 'select', label: 'Finish', options: ['chrome', 'brushed_nickel', 'matte_black', 'oil_rubbed', 'polished_nickel'] },
      has_sprayer: { type: 'checkbox', label: 'Has Sprayer' },
    },
  },
  disposal: {
    key: 'disposal',
    label: 'Garbage Disposal',
    category: 'Sink & Faucet',
    details: {
      hp: { type: 'select', label: 'Horsepower', options: ['1/3', '1/2', '3/4', '1'] },
    },
  },

  // APPLIANCES
  range: {
    key: 'range',
    label: 'Range / Stove',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['gas', 'electric', 'induction', 'dual_fuel'] },
      width: { type: 'select', label: 'Width', options: [30, 36, 48] },
      slide_in: { type: 'checkbox', label: 'Slide-In' },
    },
  },
  range_hood: {
    key: 'range_hood',
    label: 'Range Hood / Vent',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['under_cabinet', 'wall_mount', 'island', 'downdraft', 'microwave_combo'] },
      width: { type: 'select', label: 'Width', options: [30, 36, 48] },
      cfm: { type: 'number', label: 'CFM', min: 100, max: 1200 },
      vented: { type: 'select', label: 'Venting', options: ['vented', 'recirculating'] },
    },
  },
  refrigerator: {
    key: 'refrigerator',
    label: 'Refrigerator',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['french_door', 'side_by_side', 'top_freezer', 'bottom_freezer', 'counter_depth'] },
      width: { type: 'select', label: 'Width', options: [30, 33, 36] },
      built_in: { type: 'checkbox', label: 'Built-In' },
    },
  },
  dishwasher: {
    key: 'dishwasher',
    label: 'Dishwasher',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['built_in', 'drawer', 'portable'] },
      panel_ready: { type: 'checkbox', label: 'Panel Ready' },
    },
  },
  microwave: {
    key: 'microwave',
    label: 'Microwave',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['over_range', 'built_in', 'countertop', 'drawer'] },
    },
  },
  wall_oven: {
    key: 'wall_oven',
    label: 'Wall Oven',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['single', 'double', 'combo_microwave'] },
      fuel: { type: 'select', label: 'Fuel', options: ['gas', 'electric'] },
      width: { type: 'select', label: 'Width', options: [24, 27, 30] },
    },
  },
  cooktop: {
    key: 'cooktop',
    label: 'Cooktop (if separate)',
    category: 'Appliances',
    details: {
      type: { type: 'select', label: 'Type', options: ['gas', 'electric', 'induction'] },
      width: { type: 'select', label: 'Width', options: [30, 36, 48] },
      burners: { type: 'number', label: 'Burners', min: 2, max: 8 },
    },
  },

  // FLOORING & LIGHTING
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring & Lighting',
    details: {
      material: { type: 'select', label: 'Material', options: ['lvp', 'tile', 'hardwood', 'laminate', 'vinyl'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 1, max: 500 },
    },
  },
  lighting: {
    key: 'lighting',
    label: 'Lighting',
    category: 'Flooring & Lighting',
    details: {
      recessed_count: { type: 'number', label: 'Recessed Lights', min: 0, max: 20 },
      pendant_count: { type: 'number', label: 'Pendants', min: 0, max: 6 },
      under_cabinet: { type: 'checkbox', label: 'Under Cabinet' },
      chandelier: { type: 'checkbox', label: 'Chandelier' },
    },
  },
};

// ============================================
// LIVING ROOM / BEDROOM / DINING / HALL FIXTURES (12 Items)
// ============================================
export const LIVING_FIXTURES = {
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring',
    details: {
      material: { type: 'select', label: 'Material', options: ['hardwood', 'lvp', 'carpet', 'tile', 'laminate'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 1, max: 1000 },
    },
  },
  baseboards: {
    key: 'baseboards',
    label: 'Baseboards',
    category: 'Trim',
    details: {
      material: { type: 'select', label: 'Material', options: ['mdf', 'wood', 'pvc'] },
      height_inches: { type: 'select', label: 'Height', options: [3, 4, 5, 6, 8] },
      style: { type: 'select', label: 'Style', options: ['flat', 'colonial', 'craftsman'] },
    },
  },
  crown_molding: {
    key: 'crown_molding',
    label: 'Crown Molding',
    category: 'Trim',
    details: {
      material: { type: 'select', label: 'Material', options: ['mdf', 'wood', 'pvc', 'foam'] },
      size_inches: { type: 'select', label: 'Size', options: [3, 4, 5, 6] },
    },
  },
  walls: {
    key: 'walls',
    label: 'Walls',
    category: 'Walls & Ceiling',
    details: {
      type: { type: 'select', label: 'Type', options: ['paint', 'wallpaper', 'wainscot', 'accent_wall'] },
      texture: { type: 'select', label: 'Texture', options: ['smooth', 'orange_peel', 'knockdown'] },
    },
  },
  ceiling: {
    key: 'ceiling',
    label: 'Ceiling',
    category: 'Walls & Ceiling',
    details: {
      type: { type: 'select', label: 'Type', options: ['flat', 'tray', 'coffered', 'vaulted', 'popcorn'] },
      height_ft: { type: 'number', label: 'Height (ft)', min: 7, max: 20 },
    },
  },
  ceiling_light: {
    key: 'ceiling_light',
    label: 'Ceiling Light',
    category: 'Lighting',
    details: {
      type: { type: 'select', label: 'Type', options: ['flush', 'semi_flush', 'chandelier', 'fan_light', 'recessed', 'pendant'] },
      count: { type: 'number', label: 'Count', min: 1, max: 10 },
    },
  },
  ceiling_fan: {
    key: 'ceiling_fan',
    label: 'Ceiling Fan',
    category: 'Lighting',
    details: {
      has_light: { type: 'checkbox', label: 'Has Light' },
      blade_span: { type: 'select', label: 'Blade Span', options: [42, 52, 60] },
    },
  },
  windows: {
    key: 'windows',
    label: 'Windows',
    category: 'Openings',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 20 },
      type: { type: 'select', label: 'Type', options: ['double_hung', 'casement', 'sliding', 'picture', 'bay'] },
      size: { type: 'select', label: 'Size', options: ['small', 'medium', 'large'] },
    },
  },
  interior_doors: {
    key: 'interior_doors',
    label: 'Interior Doors',
    category: 'Openings',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 10 },
      type: { type: 'select', label: 'Type', options: ['hollow', 'solid', 'glass', 'french', 'barn'] },
      style: { type: 'select', label: 'Style', options: ['panel', 'flat', 'shaker'] },
    },
  },
  closet: {
    key: 'closet',
    label: 'Closet',
    category: 'Storage',
    details: {
      type: { type: 'select', label: 'Type', options: ['reach_in', 'walk_in', 'wardrobe'] },
      has_system: { type: 'checkbox', label: 'Has Closet System' },
      linear_feet: { type: 'number', label: 'Linear Feet', min: 1, max: 50 },
    },
  },
  fireplace: {
    key: 'fireplace',
    label: 'Fireplace',
    category: 'Features',
    details: {
      type: { type: 'select', label: 'Type', options: ['wood', 'gas', 'electric', 'none'] },
      surround: { type: 'select', label: 'Surround', options: ['tile', 'stone', 'brick', 'wood'] },
      mantle: { type: 'checkbox', label: 'Has Mantle' },
    },
  },
  built_ins: {
    key: 'built_ins',
    label: 'Built-Ins / Shelving',
    category: 'Features',
    details: {
      type: { type: 'select', label: 'Type', options: ['bookshelves', 'entertainment', 'window_seat'] },
      linear_feet: { type: 'number', label: 'Linear Feet', min: 1, max: 50 },
    },
  },
};

// Bedroom extends Living with outlets
export const BEDROOM_FIXTURES = {
  ...LIVING_FIXTURES,
  outlets: {
    key: 'outlets',
    label: 'Electrical Outlets',
    category: 'Electrical',
    details: {
      standard_count: { type: 'number', label: 'Standard Outlets', min: 0, max: 20 },
      usb_count: { type: 'number', label: 'USB Outlets', min: 0, max: 10 },
    },
  },
};

// ============================================
// LAUNDRY FIXTURES (8 Items)
// ============================================
export const LAUNDRY_FIXTURES = {
  washer_hookup: {
    key: 'washer_hookup',
    label: 'Washer Hookup',
    category: 'Hookups',
    details: {
      type: { type: 'select', label: 'Type', options: ['standard', 'recessed_box'] },
      has_drain: { type: 'checkbox', label: 'Has Drain' },
    },
  },
  dryer_hookup: {
    key: 'dryer_hookup',
    label: 'Dryer Hookup',
    category: 'Hookups',
    details: {
      type: { type: 'select', label: 'Type', options: ['electric_240', 'gas'] },
      vent_type: { type: 'select', label: 'Vent Type', options: ['exterior', 'ventless'] },
    },
  },
  utility_sink: {
    key: 'utility_sink',
    label: 'Utility Sink',
    category: 'Plumbing',
    details: {
      material: { type: 'select', label: 'Material', options: ['stainless', 'composite', 'acrylic'] },
      size: { type: 'select', label: 'Size', options: ['small', 'large', 'double'] },
    },
  },
  cabinets: {
    key: 'cabinets',
    label: 'Cabinets',
    category: 'Storage',
    details: {
      upper_lf: { type: 'number', label: 'Upper Linear Feet', min: 0, max: 20 },
      lower_lf: { type: 'number', label: 'Lower Linear Feet', min: 0, max: 20 },
    },
  },
  countertop: {
    key: 'countertop',
    label: 'Countertop / Folding Area',
    category: 'Storage',
    details: {
      material: { type: 'select', label: 'Material', options: ['laminate', 'butcher_block', 'quartz', 'solid_surface'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 1, max: 50 },
    },
  },
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring',
    details: {
      material: { type: 'select', label: 'Material', options: ['tile', 'lvp', 'vinyl', 'concrete'] },
      has_drain: { type: 'checkbox', label: 'Has Floor Drain' },
    },
  },
  ceiling_light: {
    key: 'ceiling_light',
    label: 'Lighting',
    category: 'Electrical',
    details: {
      type: { type: 'select', label: 'Type', options: ['flush', 'recessed', 'fluorescent', 'led_panel'] },
    },
  },
  hanging_rod: {
    key: 'hanging_rod',
    label: 'Hanging Rod',
    category: 'Storage',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 1, max: 20 },
    },
  },
};

// ============================================
// MUDROOM FIXTURES (6 Items)
// ============================================
export const MUDROOM_FIXTURES = {
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring',
    details: {
      material: { type: 'select', label: 'Material', options: ['tile', 'lvp', 'slate', 'brick'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 1, max: 200 },
    },
  },
  bench: {
    key: 'bench',
    label: 'Bench',
    category: 'Storage',
    details: {
      type: { type: 'select', label: 'Type', options: ['built_in', 'freestanding'] },
      length_inches: { type: 'number', label: 'Length (inches)', min: 24, max: 120 },
      has_storage: { type: 'checkbox', label: 'Has Storage' },
    },
  },
  cubbies: {
    key: 'cubbies',
    label: 'Cubbies / Lockers',
    category: 'Storage',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 10 },
      type: { type: 'select', label: 'Type', options: ['open', 'closed', 'mixed'] },
    },
  },
  hooks: {
    key: 'hooks',
    label: 'Coat Hooks',
    category: 'Storage',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 20 },
      type: { type: 'select', label: 'Type', options: ['individual', 'rail'] },
    },
  },
  cabinets: {
    key: 'cabinets',
    label: 'Upper Cabinets',
    category: 'Storage',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 1, max: 20 },
    },
  },
  ceiling_light: {
    key: 'ceiling_light',
    label: 'Lighting',
    category: 'Electrical',
    details: {
      type: { type: 'select', label: 'Type', options: ['flush', 'recessed', 'pendant'] },
    },
  },
};

// ============================================
// GARAGE FIXTURES (10 Items)
// ============================================
export const GARAGE_FIXTURES = {
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring',
    details: {
      current: { type: 'select', label: 'Current', options: ['bare_concrete', 'painted', 'epoxy', 'tile'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 100, max: 2000 },
      cracks: { type: 'select', label: 'Cracks', options: ['none', 'minor', 'major'] },
    },
  },
  garage_door: {
    key: 'garage_door',
    label: 'Garage Door(s)',
    category: 'Doors',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 4 },
      width_ft: { type: 'select', label: 'Width (ft)', options: [8, 9, 10, 16, 18] },
      height_ft: { type: 'select', label: 'Height (ft)', options: [7, 8] },
      material: { type: 'select', label: 'Material', options: ['steel', 'wood', 'aluminum', 'fiberglass'] },
      insulated: { type: 'checkbox', label: 'Insulated' },
      windows: { type: 'checkbox', label: 'Has Windows' },
    },
  },
  garage_opener: {
    key: 'garage_opener',
    label: 'Garage Door Opener',
    category: 'Doors',
    details: {
      type: { type: 'select', label: 'Type', options: ['chain', 'belt', 'screw', 'wall_mount'] },
      smart_enabled: { type: 'checkbox', label: 'Smart Enabled' },
      count: { type: 'number', label: 'Count', min: 0, max: 4 },
    },
  },
  service_door: {
    key: 'service_door',
    label: 'Service Door (Side Entry)',
    category: 'Doors',
    details: {
      exists: { type: 'checkbox', label: 'Exists' },
      type: { type: 'select', label: 'Type', options: ['steel', 'fiberglass', 'wood'] },
    },
  },
  walls: {
    key: 'walls',
    label: 'Walls',
    category: 'Walls & Ceiling',
    details: {
      finished: { type: 'checkbox', label: 'Finished' },
      type: { type: 'select', label: 'Type', options: ['drywall', 'osb', 'bare_studs'] },
    },
  },
  ceiling: {
    key: 'ceiling',
    label: 'Ceiling',
    category: 'Walls & Ceiling',
    details: {
      finished: { type: 'checkbox', label: 'Finished' },
      type: { type: 'select', label: 'Type', options: ['drywall', 'exposed', 'drop'] },
    },
  },
  electrical: {
    key: 'electrical',
    label: 'Electrical',
    category: 'Electrical',
    details: {
      outlet_count: { type: 'number', label: 'Outlet Count', min: 0, max: 20 },
      has_240v: { type: 'checkbox', label: 'Has 240V' },
      subpanel: { type: 'checkbox', label: 'Has Subpanel' },
    },
  },
  lighting: {
    key: 'lighting',
    label: 'Lighting',
    category: 'Electrical',
    details: {
      type: { type: 'select', label: 'Type', options: ['fluorescent', 'led', 'bare_bulb'] },
      count: { type: 'number', label: 'Count', min: 0, max: 20 },
    },
  },
  storage: {
    key: 'storage',
    label: 'Storage Systems',
    category: 'Storage',
    details: {
      shelving_lf: { type: 'number', label: 'Shelving Linear Feet', min: 0, max: 100 },
      cabinets: { type: 'checkbox', label: 'Has Cabinets' },
      ceiling_storage: { type: 'checkbox', label: 'Ceiling Storage' },
      workbench: { type: 'checkbox', label: 'Has Workbench' },
    },
  },
  hvac: {
    key: 'hvac',
    label: 'HVAC',
    category: 'Mechanical',
    details: {
      heated: { type: 'checkbox', label: 'Heated' },
      cooled: { type: 'checkbox', label: 'Cooled' },
      type: { type: 'select', label: 'Type', options: ['mini_split', 'forced_air', 'radiant'] },
    },
  },
};

// ============================================
// BASEMENT FIXTURES (12 Items)
// ============================================
export const BASEMENT_FIXTURES = {
  floor: {
    key: 'floor',
    label: 'Floor',
    category: 'Flooring',
    details: {
      current: { type: 'select', label: 'Current', options: ['bare_concrete', 'painted', 'epoxy', 'carpet', 'lvp', 'tile'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 100, max: 3000 },
      moisture_issues: { type: 'checkbox', label: 'Moisture Issues' },
    },
  },
  walls: {
    key: 'walls',
    label: 'Walls',
    category: 'Walls & Ceiling',
    details: {
      type: { type: 'select', label: 'Type', options: ['block', 'poured', 'stone', 'framed'] },
      finished: { type: 'checkbox', label: 'Finished' },
      insulated: { type: 'checkbox', label: 'Insulated' },
      moisture_issues: { type: 'checkbox', label: 'Moisture Issues' },
    },
  },
  ceiling: {
    key: 'ceiling',
    label: 'Ceiling',
    category: 'Walls & Ceiling',
    details: {
      type: { type: 'select', label: 'Type', options: ['exposed', 'drywall', 'drop', 'spray'] },
      height_ft: { type: 'number', label: 'Height (ft)', min: 6, max: 12 },
    },
  },
  stairs: {
    key: 'stairs',
    label: 'Stairs',
    category: 'Access',
    details: {
      type: { type: 'select', label: 'Type', options: ['open', 'closed', 'spiral'] },
      material: { type: 'select', label: 'Material', options: ['wood', 'concrete', 'carpet'] },
      handrail: { type: 'checkbox', label: 'Has Handrail' },
    },
  },
  egress_window: {
    key: 'egress_window',
    label: 'Egress Window(s)',
    category: 'Access',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 4 },
      has_well: { type: 'checkbox', label: 'Has Window Well' },
    },
  },
  windows: {
    key: 'windows',
    label: 'Standard Windows',
    category: 'Access',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 10 },
      type: { type: 'select', label: 'Type', options: ['hopper', 'slider', 'glass_block'] },
    },
  },
  sump_pump: {
    key: 'sump_pump',
    label: 'Sump Pump',
    category: 'Waterproofing',
    details: {
      exists: { type: 'checkbox', label: 'Exists' },
      has_backup: { type: 'checkbox', label: 'Has Backup' },
    },
  },
  waterproofing: {
    key: 'waterproofing',
    label: 'Waterproofing',
    category: 'Waterproofing',
    details: {
      interior_drain: { type: 'checkbox', label: 'Interior Drain' },
      exterior_drain: { type: 'checkbox', label: 'Exterior Drain' },
      vapor_barrier: { type: 'checkbox', label: 'Vapor Barrier' },
    },
  },
  hvac: {
    key: 'hvac',
    label: 'HVAC',
    category: 'Mechanical',
    details: {
      heated: { type: 'checkbox', label: 'Heated' },
      cooled: { type: 'checkbox', label: 'Cooled' },
      ductwork: { type: 'checkbox', label: 'Has Ductwork' },
    },
  },
  electrical: {
    key: 'electrical',
    label: 'Electrical',
    category: 'Electrical',
    details: {
      panel_location: { type: 'checkbox', label: 'Panel in Basement' },
      outlet_count: { type: 'number', label: 'Outlet Count', min: 0, max: 20 },
      needs_subpanel: { type: 'checkbox', label: 'Needs Subpanel' },
    },
  },
  plumbing: {
    key: 'plumbing',
    label: 'Plumbing Rough-In',
    category: 'Plumbing',
    details: {
      bathroom_rough: { type: 'checkbox', label: 'Bathroom Rough-In' },
      kitchen_rough: { type: 'checkbox', label: 'Kitchen Rough-In' },
      laundry_rough: { type: 'checkbox', label: 'Laundry Rough-In' },
    },
  },
  lighting: {
    key: 'lighting',
    label: 'Lighting',
    category: 'Electrical',
    details: {
      type: { type: 'select', label: 'Type', options: ['bare_bulb', 'fluorescent', 'recessed', 'led'] },
      count: { type: 'number', label: 'Count', min: 0, max: 20 },
    },
  },
};

// ============================================
// ATTIC FIXTURES (6 Items)
// ============================================
export const ATTIC_FIXTURES = {
  access: {
    key: 'access',
    label: 'Access',
    category: 'Access',
    details: {
      type: { type: 'select', label: 'Type', options: ['scuttle', 'pull_down', 'stairs'] },
      location: { type: 'text', label: 'Location' },
      size: { type: 'select', label: 'Size', options: ['standard', 'wide'] },
    },
  },
  floor: {
    key: 'floor',
    label: 'Floor / Decking',
    category: 'Structure',
    details: {
      type: { type: 'select', label: 'Type', options: ['none', 'partial', 'full'] },
      material: { type: 'select', label: 'Material', options: ['plywood', 'osb', 'none'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 0, max: 2000 },
    },
  },
  insulation: {
    key: 'insulation',
    label: 'Insulation',
    category: 'Insulation',
    details: {
      type: { type: 'select', label: 'Type', options: ['fiberglass_batt', 'blown', 'spray_foam', 'none'] },
      depth_inches: { type: 'number', label: 'Depth (inches)', min: 0, max: 24 },
      r_value: { type: 'number', label: 'R-Value', min: 0, max: 60 },
    },
  },
  ventilation: {
    key: 'ventilation',
    label: 'Ventilation',
    category: 'Ventilation',
    details: {
      ridge_vent: { type: 'checkbox', label: 'Ridge Vent' },
      soffit_vents: { type: 'checkbox', label: 'Soffit Vents' },
      gable_vents: { type: 'checkbox', label: 'Gable Vents' },
      powered_fan: { type: 'checkbox', label: 'Powered Fan' },
    },
  },
  electrical: {
    key: 'electrical',
    label: 'Electrical',
    category: 'Electrical',
    details: {
      has_light: { type: 'checkbox', label: 'Has Light' },
      has_outlet: { type: 'checkbox', label: 'Has Outlet' },
    },
  },
  hvac: {
    key: 'hvac',
    label: 'HVAC Equipment',
    category: 'Mechanical',
    details: {
      air_handler: { type: 'checkbox', label: 'Air Handler' },
      ductwork: { type: 'checkbox', label: 'Has Ductwork' },
    },
  },
};

// ============================================
// EXTERIOR FIXTURES (8 Items)
// ============================================
export const EXTERIOR_FIXTURES = {
  siding: {
    key: 'siding',
    label: 'Siding',
    category: 'Exterior Walls',
    details: {
      material: { type: 'select', label: 'Material', options: ['vinyl', 'fiber_cement', 'wood', 'brick', 'stone', 'stucco', 'aluminum'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 100, max: 5000 },
      stories: { type: 'number', label: 'Stories', min: 1, max: 4 },
    },
  },
  trim: {
    key: 'trim',
    label: 'Exterior Trim',
    category: 'Exterior Walls',
    details: {
      material: { type: 'select', label: 'Material', options: ['wood', 'pvc', 'aluminum', 'fiber_cement'] },
      includes_fascia: { type: 'checkbox', label: 'Includes Fascia' },
      includes_soffit: { type: 'checkbox', label: 'Includes Soffit' },
    },
  },
  windows: {
    key: 'windows',
    label: 'Windows',
    category: 'Openings',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 50 },
      type: { type: 'select', label: 'Type', options: ['double_hung', 'casement', 'sliding', 'picture'] },
      material: { type: 'select', label: 'Material', options: ['vinyl', 'wood', 'aluminum', 'fiberglass'] },
    },
  },
  entry_door: {
    key: 'entry_door',
    label: 'Entry Door(s)',
    category: 'Openings',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 5 },
      material: { type: 'select', label: 'Material', options: ['fiberglass', 'steel', 'wood'] },
      has_sidelights: { type: 'checkbox', label: 'Has Sidelights' },
      has_transom: { type: 'checkbox', label: 'Has Transom' },
    },
  },
  patio_door: {
    key: 'patio_door',
    label: 'Patio Door(s)',
    category: 'Openings',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 5 },
      type: { type: 'select', label: 'Type', options: ['sliding', 'french', 'bifold'] },
    },
  },
  gutters: {
    key: 'gutters',
    label: 'Gutters & Downspouts',
    category: 'Drainage',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 0, max: 500 },
      material: { type: 'select', label: 'Material', options: ['aluminum', 'copper', 'vinyl', 'steel'] },
      size: { type: 'select', label: 'Size', options: ['5_inch', '6_inch'] },
      guards: { type: 'checkbox', label: 'Has Guards' },
    },
  },
  exterior_lighting: {
    key: 'exterior_lighting',
    label: 'Exterior Lighting',
    category: 'Lighting',
    details: {
      porch_lights: { type: 'number', label: 'Porch Lights', min: 0, max: 10 },
      wall_sconces: { type: 'number', label: 'Wall Sconces', min: 0, max: 10 },
      flood_lights: { type: 'number', label: 'Flood Lights', min: 0, max: 10 },
      landscape_lights: { type: 'number', label: 'Landscape Lights', min: 0, max: 20 },
    },
  },
  paint: {
    key: 'paint',
    label: 'Exterior Paint',
    category: 'Finish',
    details: {
      body_color: { type: 'text', label: 'Body Color' },
      trim_color: { type: 'text', label: 'Trim Color' },
      last_painted: { type: 'text', label: 'Last Painted' },
    },
  },
};

// ============================================
// ROOF FIXTURES (8 Items)
// ============================================
export const ROOF_FIXTURES = {
  roofing: {
    key: 'roofing',
    label: 'Roofing Material',
    category: 'Roofing',
    details: {
      material: { type: 'select', label: 'Material', options: ['asphalt_3tab', 'architectural', 'metal', 'tile', 'slate', 'flat'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 100, max: 10000 },
      squares: { type: 'number', label: 'Squares (100 SF)', min: 1, max: 100 },
      age_years: { type: 'number', label: 'Age (years)', min: 0, max: 50 },
      layers: { type: 'number', label: 'Existing Layers', min: 1, max: 3 },
    },
  },
  pitch: {
    key: 'pitch',
    label: 'Roof Pitch',
    category: 'Roofing',
    details: {
      pitch: { type: 'select', label: 'Pitch', options: ['3/12', '4/12', '5/12', '6/12', '8/12', '10/12', '12/12'] },
      walkable: { type: 'checkbox', label: 'Walkable' },
    },
  },
  decking: {
    key: 'decking',
    label: 'Roof Decking',
    category: 'Structure',
    details: {
      material: { type: 'select', label: 'Material', options: ['plywood', 'osb', 'planks'] },
      condition: { type: 'select', label: 'Condition', options: ['good', 'needs_repair', 'needs_replacement'] },
    },
  },
  flashing: {
    key: 'flashing',
    label: 'Flashing',
    category: 'Waterproofing',
    details: {
      valleys: { type: 'checkbox', label: 'Valleys' },
      chimney: { type: 'checkbox', label: 'Chimney' },
      skylights: { type: 'checkbox', label: 'Skylights' },
      pipe_boots: { type: 'number', label: 'Pipe Boots', min: 0, max: 20 },
    },
  },
  ridge_vent: {
    key: 'ridge_vent',
    label: 'Ridge Vent',
    category: 'Ventilation',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 0, max: 200 },
      type: { type: 'select', label: 'Type', options: ['shingle_over', 'aluminum'] },
    },
  },
  skylights: {
    key: 'skylights',
    label: 'Skylights',
    category: 'Features',
    details: {
      count: { type: 'number', label: 'Count', min: 0, max: 10 },
      type: { type: 'select', label: 'Type', options: ['fixed', 'venting', 'tubular'] },
      size: { type: 'text', label: 'Size' },
    },
  },
  chimney: {
    key: 'chimney',
    label: 'Chimney',
    category: 'Features',
    details: {
      exists: { type: 'checkbox', label: 'Exists' },
      material: { type: 'select', label: 'Material', options: ['brick', 'stone', 'metal'] },
      cap: { type: 'checkbox', label: 'Has Cap' },
      flashing_condition: { type: 'select', label: 'Flashing Condition', options: ['good', 'needs_repair', 'needs_replacement'] },
    },
  },
  gutters: {
    key: 'gutters',
    label: 'Gutters (from Roof)',
    category: 'Drainage',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 0, max: 500 },
    },
  },
};

// ============================================
// DECK / PATIO FIXTURES (6 Items)
// ============================================
export const DECK_FIXTURES = {
  decking: {
    key: 'decking',
    label: 'Decking Surface',
    category: 'Surface',
    details: {
      material: { type: 'select', label: 'Material', options: ['pressure_treated', 'cedar', 'composite', 'pvc', 'ipe', 'concrete'] },
      square_feet: { type: 'number', label: 'Square Feet', min: 50, max: 2000 },
      board_width: { type: 'select', label: 'Board Width', options: ['5.5', '6'] },
      pattern: { type: 'select', label: 'Pattern', options: ['standard', 'diagonal', 'herringbone'] },
    },
  },
  framing: {
    key: 'framing',
    label: 'Deck Framing',
    category: 'Structure',
    details: {
      material: { type: 'select', label: 'Material', options: ['pressure_treated', 'steel'] },
      joist_spacing: { type: 'select', label: 'Joist Spacing', options: ['12', '16', '24'] },
      condition: { type: 'select', label: 'Condition', options: ['good', 'needs_repair', 'needs_replacement'] },
    },
  },
  railing: {
    key: 'railing',
    label: 'Railing',
    category: 'Railing',
    details: {
      linear_feet: { type: 'number', label: 'Linear Feet', min: 0, max: 200 },
      material: { type: 'select', label: 'Material', options: ['wood', 'composite', 'aluminum', 'cable', 'glass'] },
      post_type: { type: 'select', label: 'Post Type', options: ['wood', 'aluminum', 'composite'] },
    },
  },
  stairs: {
    key: 'stairs',
    label: 'Stairs',
    category: 'Access',
    details: {
      count: { type: 'number', label: 'Stair Sections', min: 0, max: 4 },
      steps_per: { type: 'number', label: 'Steps Per Section', min: 1, max: 20 },
      material: { type: 'select', label: 'Material', options: ['same_as_deck', 'concrete'] },
    },
  },
  footings: {
    key: 'footings',
    label: 'Footings / Foundation',
    category: 'Structure',
    details: {
      type: { type: 'select', label: 'Type', options: ['concrete', 'helical', 'sono_tube', 'blocks'] },
      count: { type: 'number', label: 'Count', min: 1, max: 30 },
    },
  },
  features: {
    key: 'features',
    label: 'Features',
    category: 'Features',
    details: {
      pergola: { type: 'checkbox', label: 'Pergola' },
      lighting: { type: 'checkbox', label: 'Lighting' },
      built_in_seating: { type: 'checkbox', label: 'Built-In Seating' },
      planter_boxes: { type: 'checkbox', label: 'Planter Boxes' },
    },
  },
};

// ============================================
// CUSTOM FIXTURES
// ============================================
export const CUSTOM_FIXTURES = {
  custom_item: {
    key: 'custom_item',
    label: 'Custom Item',
    category: 'Custom',
    details: {
      name: { type: 'text', label: 'Item Name' },
      description: { type: 'textarea', label: 'Description' },
    },
  },
};

// ============================================
// FIXTURE MAP BY AREA TYPE
// ============================================
export const FIXTURES_BY_AREA_TYPE = {
  bathroom: BATHROOM_FIXTURES,
  kitchen: KITCHEN_FIXTURES,
  bedroom: BEDROOM_FIXTURES,
  living: LIVING_FIXTURES,
  dining_room: LIVING_FIXTURES,
  office: LIVING_FIXTURES,
  hall: LIVING_FIXTURES,
  laundry: LAUNDRY_FIXTURES,
  mudroom: MUDROOM_FIXTURES,
  garage: GARAGE_FIXTURES,
  basement: BASEMENT_FIXTURES,
  attic: ATTIC_FIXTURES,
  exterior: EXTERIOR_FIXTURES,
  roof: ROOF_FIXTURES,
  deck: DECK_FIXTURES,
  custom: CUSTOM_FIXTURES,
};

// Helper to get fixtures for an area type
export const getFixturesForAreaType = (areaType) => {
  return FIXTURES_BY_AREA_TYPE[areaType] || CUSTOM_FIXTURES;
};

// Helper to get fixture categories for an area type
export const getFixtureCategoriesForAreaType = (areaType) => {
  const fixtures = getFixturesForAreaType(areaType);
  const categories = {};
  
  Object.values(fixtures).forEach(fixture => {
    if (!categories[fixture.category]) {
      categories[fixture.category] = [];
    }
    categories[fixture.category].push(fixture);
  });
  
  return categories;
};

// Helper to initialize empty fixture state
export const getEmptyFixtureState = (areaType) => {
  const fixtures = getFixturesForAreaType(areaType);
  const state = {};
  
  Object.keys(fixtures).forEach(key => {
    state[key] = {
      condition: 'na',
      details: {},
      notes: '',
    };
  });
  
  return state;
};

export default {
  FIXTURE_CONDITIONS,
  FIXTURES_BY_AREA_TYPE,
  getFixturesForAreaType,
  getFixtureCategoriesForAreaType,
  getEmptyFixtureState,
};
